import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ArtistPortalDimensionSeries, ArtistPortalDimensionTotal } from 'fym-shared/src/smartlinks/artistPortals';
import { TypedMemo } from '../../../common/types';
import { DeltaPill } from '../../components/primitives/DeltaPill';
import { DimensionRowModel } from '../dimensionRows';
import { changePctOf, formatChangePct, formatStreamingNumber } from '../streamingFormat';
import { streamingTotalLabels } from '../streamingTotalsMessages';
import { streamingBlockMessages as messages } from '../streamingBlockMessages';
import { StreamingBlock } from './StreamingBlock';
import { StreamingLineChart } from './StreamingLineChart';
import { DimensionName } from './DimensionName';
import { Panel } from '../../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface DimensionDetailProps {
  dimension: 'store' | 'country';
  row: DimensionRowModel;
  total: ArtistPortalDimensionTotal;
  count: number;
  series: ArtistPortalDimensionSeries | undefined;
  onClose: () => void;
}

/**
 * The detail of one store or country (design c1, c2): streams and downloads with the change, the
 * share, the rank, and the streams per day. The source has no split of a store by country, so the
 * detail shows no "Top countries on {store}".
 */
const DimensionDetailComponent: React.FC<DimensionDetailProps> = ({
  dimension,
  row,
  total,
  count,
  series,
  onClose,
}) => {
  const intl = useIntl();
  const chart = useMemo(() => {
    const points = series?.points ?? [];
    return {
      rows: points.map((point) => ({ date: point.date, total: point.streams })),
      lines: points.length === 0 ? [] : [{ key: 'total', label: 'total', kind: 'total' as const }],
    };
  }, [series]);
  const streamsChange = formatChangePct(intl, changePctOf(total.streams, total.previousStreams));
  const streamsLabel = intl.formatMessage(streamingTotalLabels.streams);

  return (
    <StreamingBlock
      title={intl.formatMessage(dimension === 'store' ? messages.colStore : messages.colCountry)}
      moreLabel={intl.formatMessage(messages.closeDetail)}
      onMore={onClose}
      testId="streaming-dimension-detail"
    >
      <div className={styles.rankName}>
        <DimensionName dimension={dimension} value={row.value} />
      </div>
      <div className={styles.tiles}>
        <Panel nested className={`${styles.tile} ${styles.tileSmall}`}>
          <span className={styles.tileLabel}>{streamsLabel}</span>
          <span className={styles.tileValue}>{formatStreamingNumber(intl, total.streams)}</span>
          {streamsChange !== null ? (
            <DeltaPill value={changePctOf(total.streams, total.previousStreams) ?? 0} label={streamsChange} />
          ) : null}
        </Panel>
        <Panel nested className={`${styles.tile} ${styles.tileSmall}`}>
          <span className={styles.tileLabel}>{intl.formatMessage(streamingTotalLabels.downloads)}</span>
          <span className={styles.tileValue}>{formatStreamingNumber(intl, total.downloads)}</span>
        </Panel>
        <Panel nested className={`${styles.tile} ${styles.tileSmall}`}>
          <span className={styles.tileLabel}>{intl.formatMessage(messages.colShare)}</span>
          <span className={styles.tileValue}>
            {row.share === null ? '—' : intl.formatNumber(row.share, { style: 'percent', maximumFractionDigits: 1 })}
          </span>
        </Panel>
        <Panel nested className={`${styles.tile} ${styles.tileSmall}`}>
          <span className={styles.tileLabel}>{intl.formatMessage(messages.rank)}</span>
          <span className={styles.tileValue}>{intl.formatMessage(messages.rankOf, { rank: row.rank, count })}</span>
        </Panel>
      </div>
      <StreamingLineChart data={chart} totalLabel={streamsLabel} height={200} />
    </StreamingBlock>
  );
};

export const DimensionDetail = TypedMemo(DimensionDetailComponent);
